// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import updateBillTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateBillTo";
import updateShipTo from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateShipTo";
import translate from "@insite/client-framework/Translate";
import { BillToModel, ShipToModel, WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import BillingAddress, { BillingAddressStyles } from "@insite/content-library/Widgets/CheckoutShipping/BillingAddress";
import PickUpAddress, { PickUpAddressStyles } from "@insite/content-library/Widgets/CheckoutShipping/PickUpAddress";
import ShippingAddress, {
    ShippingAddressStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/ShippingAddress";
import DatePicker from "@insite/mobius/DatePicker";
import { GridItemProps } from "@insite/mobius/GridItem";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updateXCheckoutState from "@pace/App/Handlers/Checkout/UpdateLoacalState";
import xUseTimeSlots, { getDateTime, setDateTime } from "@pace/App/Hooks/xUseTimeSlots";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { CHECKOUT_WHEN_TO_DELIVER } from "@pace/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { DatePickerState } from "@pace/MobiusOverrides/DatePicker";
import Select from "@pace/MobiusOverrides/Select";
import IconClock from "@pace/XDL/Icons/IconClock";
import { parseBoolean } from "@pace/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const shipTosDataView = cart
        ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
        : undefined;
    let newAddress;
    let oneTimeAddress;
    let isShipToSameAsBillTo = false;
    let canUseBillToAsShipTo = false;

    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        newAddress = shipTos!.find(s => s.isNew && !s.oneTimeAddress);
        oneTimeAddress = shipTos!.find(s => s.oneTimeAddress);
        isShipToSameAsBillTo = cart.billToId === cart.shipToId;
        canUseBillToAsShipTo = shipTos!.some(shipTo => shipTo.id === cart.billToId);
    }

    return {
        cart,
        newAddress,
        oneTimeAddress,
        shipToState: getShipToState(state, cart ? cart.shipToId : undefined),
        shipTosDataView,
        isShipToSameAsBillTo,
        session: state.context.session,
        countries: getCurrentCountries(state),
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        canUseBillToAsShipTo,
        billToState: getBillToState(state, cart?.billToId),
        cartPageLink: getPageLinkByPageType(state, "CartPage")?.url,
        // XNG-Changes
        shippingPageState: state.xCheckout.shipping,
        cartSettings: getSettingsCollection(state).cartSettings,
        requestedDeliveryDate: state.pages.checkoutReviewAndSubmit.requestedDeliveryDate,
    };
};

const mapDispatchToProps = {
    updateShipTo,
    setCurrentShipTo,
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    loadShipTo,
    loadShipTos,
    updateBillTo,
    // XNG-Changes
    updateXCheckoutState,
    setRequestedDeliveryDate,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutShippingAddressesStyles {
    loadingWrapper?: InjectableCss;
    container?: InjectableCss;
    addressItem?: GridItemProps;
    addressContainer?: InjectableCss;
    shippingAddressGridItem?: GridItemProps;
    shippingAddress?: ShippingAddressStyles;
    billingAddressGridItem?: GridItemProps;
    billingAddress?: BillingAddressStyles;
    pickUpAddress?: PickUpAddressStyles;
    // XNG-Changes
    section?: InjectableCss;
    hr?: InjectableCss;
    dateTimeWrapper?: InjectableCss;
}

export const checkoutShippingAddressesStyles: CheckoutShippingAddressesStyles = {
    loadingWrapper: {
        css: css`
            display: flex;
            height: 450px;
            justify-content: center;
            align-items: center;
        `,
    },
    addressItem: { width: 12 },
    shippingAddressGridItem: { width: [12, 12, 12, 6, 6] },
    billingAddressGridItem: { width: [12, 12, 12, 6, 6] },
};

/**
 * @deprecated Use checkoutShippingAddressesStyles instead.
 */
export const fulfillmentMethodAndAddressStyles = checkoutShippingAddressesStyles;
const styles = checkoutShippingAddressesStyles;

const CheckoutShippingAddresses: FC<Props> = ({
    cart,
    shipToState,
    shipTosDataView,
    session,
    countries,
    shipToAddressFields,
    billToAddressFields,
    setCurrentShipTo,
    updatePickUpWarehouse,
    loadShipTo,
    loadShipTos,
    updateBillTo,
    billToState,
    updateShipTo,
    canUseBillToAsShipTo,
    isShipToSameAsBillTo,
    newAddress,
    oneTimeAddress,
    children,
    cartPageLink,
    history,
    // XNG-Changes
    shippingPageState,
    cartSettings,
    requestedDeliveryDate,
    updateXCheckoutState,
    setRequestedDeliveryDate,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    // Xng-Change: store min date in a state
    const [minDate, setMinDate] = React.useState(() => {
        const newMinDate = new Date();
        newMinDate.setHours(0, 0, 0, 0);
        return newMinDate;
    });

    useEffect(() => {
        if (cart && shipTosDataView && !shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos({ billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] });
        }
    });

    useEffect(() => {
        if (!shipToState.value && cart) {
            loadShipTo({ billToId: cart.billToId!, shipToId: cart.shipToId! });
        }
    });

    // XNG-Chagnes: here set requested delvery Date and Time
    const updateTimeslot = (timeslot: string) => {
        updateXCheckoutState({ shipping: { timeslot } });
        if (timeslot !== "" && requestedDeliveryDate) {
            setDateTime(requestedDeliveryDate, timeslot);
            setRequestedDeliveryDate({
                requestedDeliveryDate,
                requestedDeliveryDateDisabled: false,
            });
        }
    };
    
    // Xng-Change
    useEffect(() => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: minDate,
        });
    }, [minDate]);

    useEffect(() => {
        if (cart) {
            if (cart.requestedDeliveryDate) {
                const currentDate = new Date();
                const requestedDeliveryDate = new Date(cart.requestedDeliveryDate);
                // If saved time is less than current date clear Date

                setRequestedDeliveryDate({ requestedDeliveryDate });
                if (parseBoolean(cart.properties.deliverAnytime)) {
                    updateTimeslot("");
                } else {
                    updateTimeslot(getDateTime(requestedDeliveryDate));
                }
            }
        }
    }, [cart]);

    // XNG-Chagnes: remove loading from here and move it to Page Container
    if (!cart) {
        return null;
    }

    if (!countries || !shipToState.value || !billToState.value) {
        return null;
    }

    const { billToId } = cart;
    const { fulfillmentMethod } = session;

    const changeShippingAddressHandler = (address: ShipToModel, isEdit: boolean) => {
        updateShipTo({
            billToId: billToId!,
            shipTo: address,
            isEdit,
            onSuccess: hasCartlines => {
                if (!hasCartlines) {
                    history.push(cartPageLink!);
                }
            },
        });
    };

    const changeBillingAddressHandler = (address: BillToModel, isEdit: boolean) => {
        updateBillTo({
            billTo: address,
            isEdit,
            onSuccess: hasCartlines => {
                if (!hasCartlines) {
                    history.push(cartPageLink!);
                }
            },
        });
    };

    const useBillingAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setCurrentShipTo({ shipToId: cart.billToId! });
    };

    const handlePickUpAddressChange = async (pickUpWarehouse: WarehouseModel) => {
        // XNG-Changes: 
        updateXCheckoutState({ shipping: { loading: true } });
        await updatePickUpWarehouse({ pickUpWarehouse });
        updateXCheckoutState({ shipping: { loading: false } });
        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
    };
    // <XNG-Changes>
    const handleWhenToDeliverChange = (e: any) => {
        updateXCheckoutState({ shipping: { shipPartial: e.target.value } });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        if (shippingPageState.timeslot !== "" && selectedDay) {
            setDateTime(selectedDay, shippingPageState.timeslot);
        }
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };

    const { timeSlots } = xUseTimeSlots({
        date: requestedDeliveryDate,
    });
    // </XNG-Changes>

    // XNG-Changes: structure
    return (
        <StyledWrapper {...styles.container}>
            {fulfillmentMethod === FulfillmentMethod.Ship && (
                <>
                    {shipToAddressFields && (
                        <StyledWrapper {...styles.section}>
                            <Typography variant="h4" forwardAs="h2">
                                {translate("Delivery Info")}
                            </Typography>
                            <ShippingAddress
                                address={shipToState.value}
                                currentBillToId={cart.billToId}
                                countries={countries}
                                addressFieldDisplayCollection={shipToAddressFields}
                                onChange={changeShippingAddressHandler}
                                showUseBillingAddress={canUseBillToAsShipTo}
                                isUseBillingAddressDisabled={isShipToSameAsBillTo}
                                onClickUseBillingAddress={useBillingAddressHandler}
                                extendedStyles={styles.shippingAddress}
                                newAddress={newAddress}
                                oneTimeAddress={oneTimeAddress}
                                // XNG-Changes
                                {...{ shipToLocked: parseBoolean(billToState.value.properties.customerShipToLocked) }}
                            >
                                {children}
                            </ShippingAddress>
                        </StyledWrapper>
                    )}
                    {/** XNG-Changes: show bill to address for ship only */}
                    {billToAddressFields && (
                        <StyledWrapper {...styles.section}>
                            <BillingAddress
                                address={billToState.value}
                                onChange={changeBillingAddressHandler}
                                countries={countries}
                                addressFieldDisplayCollection={billToAddressFields}
                                extendedStyles={styles.billingAddress}
                            />
                        </StyledWrapper>
                    )}
                </>
            )}

            {fulfillmentMethod === FulfillmentMethod.PickUp && session.pickUpWarehouse && (
                <PickUpAddress
                    address={session.pickUpWarehouse}
                    onChange={handlePickUpAddressChange}
                    extendedStyles={styles.pickUpAddress}
                />
            )}

            {/** XNG-Changes: Add Preference + Requested Delivery Date / Time  */}
            {fulfillmentMethod === FulfillmentMethod.Ship && (
                <>
                    <StyledWrapper {...styles.hr} />
                    <StyledWrapper {...styles.section}>
                        <Typography variant="h4">{translate("Preference")}</Typography>
                        <RadioGroup
                            label={undefined}
                            value={shippingPageState.shipPartial}
                            onChange={handleWhenToDeliverChange}
                        >
                            <Radio value="true">{CHECKOUT_WHEN_TO_DELIVER.DeliverWhenAvailable}</Radio>
                            <Radio value="false">{CHECKOUT_WHEN_TO_DELIVER.DeliverWhenAllAvailable}</Radio>
                        </RadioGroup>
                    </StyledWrapper>
                    {cartSettings.canRequestDeliveryDate && (
                        <StyledWrapper {...styles.section} data-test-selector="checkoutShippingRequestedDeliveryDate">
                            <Typography variant="h4">{translate("Requested Delivery Date / Time")}</Typography>
                            <StyledWrapper {...styles.dateTimeWrapper}>
                                <DatePicker
                                    label={`${translate("Date")}`}
                                    // hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                                    // XNG-Changes
                                    selectedDay={requestedDeliveryDate!}
                                    dateTimePickerProps={{
                                        // XNG-Changes: set min date start from 00:00  to avoid validation
                                        minDate,
                                        maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                        monthAriaLabel: translate("Request delivery date month"),
                                        dayAriaLabel: translate("Request delivery date day"),
                                        yearAriaLabel: translate("Request delivery date year"),
                                    }}
                                    onDayChange={handleRequestDeliveryDateChanged}
                                />
                                {/** <XNG-Changes> */}
                                <Select
                                    label={translate("Time")}
                                    placeholder={translate("Anytime")}
                                    value={shippingPageState.timeslot}
                                    onChange={event => updateTimeslot(event.target.value)}
                                    leftIconProps={{
                                        src: IconClock,
                                    }}
                                    disabled={!requestedDeliveryDate}
                                >
                                    <option value="">{translate("Anytime")}</option>
                                    {timeSlots.map((value, index) => (
                                        <option key={index} value={value}>
                                            {value}
                                        </option>
                                    ))}
                                </Select>
                                {/** </XNG-Changes> */}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingAddresses)),
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage"],
        displayName: "Addresses",
    },
};

export default widgetModule;
